/* eslint-disable no-underscore-dangle */
import { useMemo, useCallback, useState, useEffect } from "react";
import { useHistory } from "react-router";
import { Helmet } from "react-helmet";
import { gql, useQuery } from "@apollo/client";

import { UserType } from "../../../../types/userType";
import Layout from "../../../../components/Layout";
import Table from "../../../../components/Table";
import { Heading } from "../../../../components/Type";
import { ArrowRight } from "../../../../components/Icons/general-interface-1";
import Button from "../../../../components/Button";
import LocationFilterDropdown from "../../../../components/FilterDropdowns/LocationFilterDropdown";
import SearchInput from "../../../../components/SearchInput";
import Loader from "../../../../components/Loader";
import CreateNewUserButton from "../CreateNewUserButton";
import DashboardNav from "../DashboardNav";
import { DateCell, StringListCell, TextCell, ProfileCell } from "../../../../components/Table/TableCells";
import styles from "../index.module.scss";
import EmptyView from "../../../../components/Table/EmptyView";
import { useStoreContext } from "../../../../contexts/StoreProvider";
import { FilterType } from "../../../../types/contextType";

const GET_USERS = gql`
  query Users(
    $limit: Int
    $sort: String
    $start: Int
    $where: JSON
    $connectionWhere: JSON
  ) {
    users(limit: $limit, sort: $sort, start: $start, where: $where) {
      id
      updated_at
      first_name
      last_name
      adp_auth_num
      locations {
        id
        name
      }
      role {
        type
        name
      }
      clients_sales_reps {
        id
      }
      users_authorizers {
        id
      }
    }
    usersConnection(where: $connectionWhere) {
      aggregate {
        count
      }
    }
  }
`;

const GET_LOCATIONS = gql`
  query Locations($limit: Int, $sort: String, $start: Int, $where: JSON) {
    locations(limit: $limit, sort: $sort, start: $start, where: $where) {
      id
      name
    }
  }
`;

type WhereType = {
  [key: string]:
    | string
    | { [key: string]: boolean | string | string[] }[]
    | string[];
};

const AdminDashboard = () => {
  const [pageSize, setPageSize] = useState(10);
  const [filters, setFilters] = useState<FilterType>({locations: null});
  const [search, setSearch] = useState<string | undefined>();
  const history = useHistory();
  const [, setBoundaryError] = useState<Error | null>(null);
  const { state: { filter }, dispatch } = useStoreContext();

  useEffect(() => {
    setFilters(filter);
  }, [filter]);

  const [userData, setUserData] = useState<{
    users: UserType[];
    count: number;
  }>({ users: [], count: 0 });
  const { data, loading, refetch } = useQuery(GET_USERS, {
    onCompleted: (data) => {
      setUserData({
        users: data.users,
        count: data.usersConnection.aggregate.count,
      });
    },
    onError: (error) => {
      setBoundaryError(() => {
        throw error;
      });
    },
  });
  const { data: locationData } = useQuery(GET_LOCATIONS, {
    onError: (error) => {
      setBoundaryError(() => {
        throw error;
      });
    },
  });

  const handleFilterChange = (
    id: string,
    filter?:
      | { value: string; label: string }
      | { value: string; label: string }[]
  ) => {
    const newFilter = {
      [id]: Array.isArray(filter) ? filter.map((f) => f.value) : filter?.value,
    };
    setFilters({ ...filters, ...newFilter });
    dispatch({
      type: "FILTER",
      payload: {...filters, ...newFilter},
    });
  };

  const fetchData = useCallback(
    ({ pageSize, pageIndex, sortBy }) => {
      const variables: {
        limit: number;
        start: number;
        sort?: unknown;
        where?: WhereType;
        connectionWhere?: WhereType;
      } = {
        limit: pageSize,
        start: pageIndex * pageSize,
      };
      if (sortBy.length) {
        if (sortBy[0].id === "status") {
          variables.sort = `client_files.status:${
            sortBy[0].desc ? "DESC" : "ASC"
          }`;
        } else if (sortBy[0].id === "locations") {
          variables.sort = `locations.name:${sortBy[0].desc ? "DESC" : "ASC"}`;
        } else {
          variables.sort = `${sortBy[0].id}:${sortBy[0].desc ? "DESC" : "ASC"}`;
        }
      }

      const where: WhereType = { "role.type": "sales_rep" };
      if (filter.locations || search) {
        const and: { [key: string]: boolean | string | string[] }[] = [];
        const or: { [key: string]: boolean | string }[] = [];
        if (filter.locations && filter.locations.length) {
          and.push({ locations_in: filter.locations });
        }
        if (search) {
          or.push({ first_name_contains: search });
          or.push({ last_name_contains: search });
        }
        if (and.length) where._and = and;
        if (or.length) where._or = or;
      }

      variables.where = where;
      variables.connectionWhere = where;

      setPageSize(pageSize);
      refetch(variables).then((response) => {
        if (response.data) {
          setUserData({
            users: response.data.users,
            count: response.data.usersConnection.aggregate.count,
          });
        }
      });
    },
    [refetch, filter, search]
  );

  const columns = useMemo(
    () => [
      {
        Header: "Name",
        accessor: "first_name",
        Cell: ({ row }) => (
          <ProfileCell
            admin
            data={row.original}
            description={row.original.adp_auth_num}
          />
        ),
      },
      {
        Header: "Last Updated",
        accessor: "updated_at",
        Cell: ({ value }) => <DateCell value={value} />,
      },
      {
        Header: "No. Clients",
        accessor: "clients_sales_reps",
        disableSortBy: true,
        Cell: ({ value }) => <TextCell value={value.length} />,
      },
      {
        Header: "Associated Authorizers",
        accessor: "users_authorizers",
        disableSortBy: true,
        Cell: ({ value }) => <TextCell value={value.length} />,
      },
      {
        Header: "Location(s)",
        accessor: "locations",
        Cell: ({ value }) => <StringListCell data={value.map((v) => v.name)} />,
      },
    ],
    []
  );

  const getCurrentFilters = useMemo(() => {
    const currentFilters = Object.keys(filters)
      .filter((f) => {
        if (filters[f] && typeof filters[f] !== "string") {
          return filters[f].length;
        }
        return !!filters[f];
      })
      .map((filter) => {
        let id;
        if (filter === "locations") {
          id = "locations";
        }
        return { id, value: filters[filter] };
      });

    if (search) {
      currentFilters.push({ id: "first_name", value: search });
    } else {
      currentFilters.filter((f) => f.id !== "first_name");
    }
    return currentFilters;
  }, [filters, search]);

  const pageCount = data ? Math.ceil(data.usersConnection.aggregate.count / pageSize) : 0;

  const filterLocations = () => {
    return locationData.locations
      .filter(loc => filters.locations?.includes(loc.id))
      .map((loc) => ({value: loc.id, label: loc.name}));
  };

  const handleResetFilters = () => {
    setFilters({locations: null});
    dispatch({
      type: "FILTER",
      payload: {locations: null},
    });

    setSearch("");
    const input = (document.getElementById("search-filter") as HTMLInputElement);
    if (input) input.value = "";
  };

  return (
    <>
      <Layout>
        <Helmet>
          <title>Motion Home</title>
        </Helmet>
        <div className={styles.headerRow}>
          <Heading size="h3" component="h1" className={styles.heading}>
            Dashboard
          </Heading>
          <Button
            primary
            icon={<ArrowRight />}
            onClick={() => history.push("/admin/client/new")}>
              Create new client file
          </Button>
          <CreateNewUserButton dashboard="sales_rep" />
        </div>
        <div className={styles.nav}>
          <DashboardNav />
        </div>
        {loading && (
          <Loader heading="Loading Motion list..." className={styles.loader} />
        )}
        <div className={loading ? styles.tableHide : styles.tableShow}>
          <div className={styles.filterRow}>
            <div className={styles.filterItem}>
              <SearchInput
                onChange={(value) => setSearch(value)}
                placeholder="Search by first or last name"
                loading={loading}
              />
            </div>
            {locationData && (
              <div className={styles.filterItem}>
                <LocationFilterDropdown
                  options={locationData.locations.map(
                    (loc: { id: string; name: string }) => ({
                      value: loc.id,
                      label: loc.name,
                    })
                  )}
                  onChange={(locations: { value: string; label: string }[]) => handleFilterChange("locations", locations) }
                  values={filterLocations()}
                />
              </div>
            )}
            <div>
              <Button
                secondary
                onClick={handleResetFilters}
              >
                Reset Filters
              </Button>
            </div>
          </div>
          <Table
            data={userData.users}
            columns={columns}
            pageCount={pageCount}
            fetchData={fetchData}
            total={userData.count}
            loading={loading}
            handleRowClick={(row) => {
              history.push(`/admin/user/${row.original.id}`);
            }}
            emptyView={
              <EmptyView
                message={
                  search
                    ? `No results found matching your search criteria.`
                    : "You do not have any clients on file yet."
                }
              >
                <Button primary onClick={() => history.push("/client/new")}>
                  Create new client file
                </Button>
              </EmptyView>
            }
            initialState={{ sortBy: [{ id: "first_name", desc: false }] }}
            filters={getCurrentFilters}
          />
        </div>
      </Layout>
    </>
  );
};

export default AdminDashboard;
